import React, { useState, useEffect } from 'react';
import { errorMonitoringService } from '../../services/errorMonitoringService';
import { AlertTriangle, CheckCircle, XCircle, AlertCircle, TrendingUp, RefreshCw, Download, Search } from 'lucide-react';
import UnifiedCard from '../../components/unified/UnifiedCard';
import UnifiedButton from '../../components/unified/UnifiedButton';
import UnifiedInput from '../../components/unified/UnifiedInput';
import UnifiedSelect from '../../components/unified/UnifiedSelect';

const ErrorMonitoringDashboard = () => {
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState({ total: 0, byType: {}, byUser: {}, recent: [] });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('24h');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedError, setSelectedError] = useState(null);

  useEffect(() => {
    loadErrorStats();
    loadBufferedErrors();
  }, [timeRange]);

  const loadErrorStats = async () => {
    setLoading(true);
    try {
      const errorStats = await errorMonitoringService?.getErrorStats(timeRange);
      setStats(errorStats);
    } catch (error) {
      console.error('Failed to load error stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBufferedErrors = () => {
    const buffered = errorMonitoringService?.getBufferedErrors();
    setErrors(buffered);
  };

  const handleRefresh = () => {
    loadErrorStats();
    loadBufferedErrors();
  };

  const handleClearBuffer = () => {
    errorMonitoringService?.clearErrorBuffer();
    setErrors([]);
  };

  const filteredErrors = errors?.filter(error => {
    const matchesType = filterType === 'all' || error?.error?.name === filterType;
    const matchesSearch = !searchQuery || 
      error?.error?.message?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      error?.context?.tags?.boundary?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesType && matchesSearch;
  });

  const errorTypes = [...new Set(errors.map(e => e?.error?.name).filter(Boolean))];

  const getErrorIcon = (type) => {
    switch (type) {
      case 'NetworkError':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case 'ValidationError':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'AuthenticationError':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString();
  };

  const exportErrors = () => {
    const dataStr = JSON.stringify(filteredErrors, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `error-report-${Date.now()}.json`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-neutral-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">Error Monitoring Dashboard</h1>
          <p className="text-neutral-600">Real-time error tracking and performance monitoring</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <UnifiedCard className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600 mb-1">Total Errors</p>
                <p className="text-2xl font-bold text-neutral-900">{stats?.total}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <div className="mt-2 flex items-center text-sm">
              <TrendingUp className="w-4 h-4 text-red-500 mr-1" />
              <span className="text-red-600">Last {timeRange}</span>
            </div>
          </UnifiedCard>

          <UnifiedCard className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600 mb-1">Buffered Errors</p>
                <p className="text-2xl font-bold text-neutral-900">{errors?.length}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-orange-500" />
            </div>
            <div className="mt-2 flex items-center text-sm">
              <span className="text-neutral-600">Not sent to Sentry</span>
            </div>
          </UnifiedCard>

          <UnifiedCard className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600 mb-1">Error Types</p>
                <p className="text-2xl font-bold text-neutral-900">{Object.keys(stats?.byType)?.length}</p>
              </div>
              <XCircle className="w-8 h-8 text-purple-500" />
            </div>
            <div className="mt-2 flex items-center text-sm">
              <span className="text-neutral-600">Unique error types</span>
            </div>
          </UnifiedCard>

          <UnifiedCard className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-600 mb-1">Monitoring Status</p>
                <p className="text-lg font-semibold text-green-600">Active</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <div className="mt-2 flex items-center text-sm">
              <span className="text-neutral-600">Sentry integrated</span>
            </div>
          </UnifiedCard>
        </div>

        {/* Controls */}
        <UnifiedCard className="p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <UnifiedInput
                placeholder="Search errors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                icon={<Search className="w-4 h-4" />}
              />
            </div>

            <UnifiedSelect
              value={timeRange}
              onChange={(e) => setTimeRange(e?.target?.value)}
              options={[
                { value: '1h', label: 'Last Hour' },
                { value: '24h', label: 'Last 24 Hours' },
                { value: '7d', label: 'Last 7 Days' },
              ]}
            />

            <UnifiedSelect
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              options={[
                { value: 'all', label: 'All Types' },
                ...errorTypes?.map(type => ({ value: type, label: type }))
              ]}
            />

            <UnifiedButton
              onClick={handleRefresh}
              variant="secondary"
              icon={<RefreshCw className="w-4 h-4" />}
            >
              Refresh
            </UnifiedButton>

            <UnifiedButton
              onClick={exportErrors}
              variant="secondary"
              icon={<Download className="w-4 h-4" />}
            >
              Export
            </UnifiedButton>

            {errors?.length > 0 && (
              <UnifiedButton
                onClick={handleClearBuffer}
                variant="danger"
              >
                Clear Buffer
              </UnifiedButton>
            )}
          </div>
        </UnifiedCard>

        {/* Error Types Breakdown */}
        {Object.keys(stats?.byType)?.length > 0 && (
          <UnifiedCard className="p-6 mb-6">
            <h2 className="text-xl font-semibold text-neutral-900 mb-4">Error Types Breakdown</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(stats?.byType)?.map(([type, count]) => (
                <div key={type} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    {getErrorIcon(type)}
                    <span className="font-medium text-neutral-900">{type}</span>
                  </div>
                  <span className="text-lg font-bold text-neutral-700">{count}</span>
                </div>
              ))}
            </div>
          </UnifiedCard>
        )}

        {/* Error List */}
        <UnifiedCard className="p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            Recent Errors ({filteredErrors?.length})
          </h2>

          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-neutral-400 animate-spin mx-auto mb-2" />
              <p className="text-neutral-600">Loading error data...</p>
            </div>
          ) : filteredErrors?.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="text-neutral-600 font-medium">No errors found</p>
              <p className="text-sm text-neutral-500 mt-1">Your application is running smoothly!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredErrors?.map((errorData, index) => (
                <div
                  key={index}
                  className="border border-neutral-200 rounded-lg p-4 hover:border-neutral-300 transition-colors cursor-pointer"
                  onClick={() => setSelectedError(errorData)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      {getErrorIcon(errorData?.error?.name)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-neutral-900">
                            {errorData?.error?.name || 'Unknown Error'}
                          </span>
                          {errorData?.context?.tags?.boundary && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              {errorData?.context?.tags?.boundary}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-neutral-700 mb-2">
                          {errorData?.error?.message || 'No error message'}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-neutral-500">
                          <span>{formatTimestamp(errorData?.timestamp)}</span>
                          {errorData?.context?.extra?.errorInfo?.componentStack && (
                            <span className="text-orange-600">Has stack trace</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </UnifiedCard>

        {/* Error Detail Modal */}
        {selectedError && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-neutral-900">Error Details</h3>
                  <button
                    onClick={() => setSelectedError(null)}
                    className="text-neutral-500 hover:text-neutral-700"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-neutral-700">Error Type</label>
                    <p className="text-neutral-900 mt-1">{selectedError?.error?.name}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-neutral-700">Message</label>
                    <p className="text-neutral-900 mt-1">{selectedError?.error?.message}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-neutral-700">Timestamp</label>
                    <p className="text-neutral-900 mt-1">{formatTimestamp(selectedError?.timestamp)}</p>
                  </div>

                  {selectedError?.error?.stack && (
                    <div>
                      <label className="text-sm font-medium text-neutral-700">Stack Trace</label>
                      <pre className="text-xs bg-neutral-900 text-neutral-100 p-4 rounded mt-1 overflow-x-auto">
                        {selectedError?.error?.stack}
                      </pre>
                    </div>
                  )}

                  {selectedError?.context && (
                    <div>
                      <label className="text-sm font-medium text-neutral-700">Context</label>
                      <pre className="text-xs bg-neutral-50 p-4 rounded mt-1 overflow-x-auto">
                        {JSON.stringify(selectedError?.context, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <UnifiedButton onClick={() => setSelectedError(null)}>
                    Close
                  </UnifiedButton>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorMonitoringDashboard;